import { InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import dayjs from "dayjs";
import { api } from "../../../singleton/axios";
import { NotificationsPage, RecentNotifications } from "../types";
import { RECENT_NOTIFICATIONS_QUERY_KEY } from "./useRecentNotifications";
import { NOTIFICATIONS_INBOX_QUERY_KEY } from "./useNotifications";

type InboxData = InfiniteData<NotificationsPage, string | null>;

/**
 * "Mark all as seen": sweeps everything out of New (the badge's set) and clears
 * the unread dots, without marking anything read. Nothing is lost (the items
 * move to Earlier), which is why it needs no confirmation and the badge can
 * drop to zero immediately.
 */
export const useClearRecent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.post("/notifications/clear-all", {});
    },

    onMutate: async () => {
      // An inbox read already in flight would land after this and put the
      // items back under New: cancel both before changing either.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: RECENT_NOTIFICATIONS_QUERY_KEY }),
        queryClient.cancelQueries({ queryKey: NOTIFICATIONS_INBOX_QUERY_KEY }),
      ]);
      const previousRecent = queryClient.getQueryData<RecentNotifications>(
        RECENT_NOTIFICATIONS_QUERY_KEY,
      );
      const previousInbox = queryClient.getQueryData<InboxData>(NOTIFICATIONS_INBOX_QUERY_KEY);
      queryClient.setQueryData<RecentNotifications>(RECENT_NOTIFICATIONS_QUERY_KEY, {
        items: [],
        count: 0,
      });
      const clearedAt = dayjs().toISOString();
      queryClient.setQueryData<InboxData>(NOTIFICATIONS_INBOX_QUERY_KEY, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            // The server clears exactly the recent set, nothing older.
            items: page.items.map((item) =>
              item.recent ? { ...item, clearedAt, recent: false } : item,
            ),
          })),
        };
      });
      return { previousRecent, previousInbox };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousRecent) {
        queryClient.setQueryData(RECENT_NOTIFICATIONS_QUERY_KEY, context.previousRecent);
      }
      if (context?.previousInbox) {
        queryClient.setQueryData(NOTIFICATIONS_INBOX_QUERY_KEY, context.previousInbox);
      }
      enqueueSnackbar("Couldn't mark your notifications as seen. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: RECENT_NOTIFICATIONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_INBOX_QUERY_KEY });
    },
  });
};
